import React, { useEffect, useId, useRef, useState } from 'react';
import classNames from 'classnames';

import getCountryCodes from '../../translations/countryCodes';
import { isFieldForUserType } from '../../util/fieldHelpers';
import { useIntl } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import { getRecordedSalesCount } from '../../util/sellerSales';
import { REVIEW_TYPE_OF_PROVIDER } from '../../util/types';

import { AvatarLarge, CountryFlag, NamedLink } from '../../components';

import css from './ProfileHeader.module.css';

const BUSINESS_TYPES = ['commercial', 'business', 'gewerblich', 'haendler', 'händler', 'dealer'];
const PRIVATE_TYPES = ['private', 'privat'];

/** Resolve profile identity fields using their public visibility and user-type configuration. */
export const getProfileIdentity = (publicData = {}, userFieldConfig = [], intl) => {
  const canDisplay = key => {
    const fieldConfigs = userFieldConfig.filter(field => field.key === key);
    return (
      fieldConfigs.length === 0 ||
      fieldConfigs.some(
        field =>
          field.scope === 'public' &&
          field.showConfig?.displayInProfile !== false &&
          isFieldForUserType(publicData.userType, field)
      )
    );
  };
  const countries = getCountryCodes(intl.locale.split('-')[0]);
  const countryField = [
    { key: 'country', value: publicData.country },
    { key: 'locationCountry', value: publicData.locationCountry },
    { key: 'sellerCountry', value: publicData.sellerCountry },
    { key: 'location', value: publicData.location?.country },
  ]
    .filter(field => canDisplay(field.key))
    .map(field => ({
      ...field,
      country: countries.find(
        country =>
          country.code ===
          String(field.value || '')
            .trim()
            .toUpperCase()
      ),
    }))
    .find(field => field.country);
  const businessField = ['isBusiness', 'business', 'is_business'].find(
    key => canDisplay(key) && typeof publicData[key] === 'boolean'
  );
  const sellerField = businessField
    ? { key: businessField, sellerType: publicData[businessField] ? 'business' : 'private' }
    : ['accountType', 'sellerType']
        .filter(canDisplay)
        .map(key => {
          const value = String(publicData[key] || '')
            .trim()
            .toLowerCase();
          const sellerType = BUSINESS_TYPES.includes(value)
            ? 'business'
            : PRIVATE_TYPES.includes(value)
            ? 'private'
            : null;
          return { key, sellerType };
        })
        .find(field => field.sellerType);

  const sales = getRecordedSalesCount(publicData, userFieldConfig);

  return {
    salesCount: sales.count,
    country: countryField?.country || null,
    sellerType: sellerField?.sellerType || null,
    // A location object may also contain a city or address that is not shown in the header.
    displayedFieldKeys: [
      countryField?.key !== 'location' ? countryField?.key : null,
      sellerField?.key,
      sales.fieldKey,
    ].filter(Boolean),
  };
};

/** Compact profile identity with public seller details and review navigation. */
const ProfileHeader = props => {
  const {
    user,
    userFieldConfig,
    displayName,
    bio,
    reviews = [],
    salesCount = null,
    salesInProgress = false,
    queryReviewsInProgress = false,
    queryReviewsError,
    hideReviews = false,
    showEditLink = false,
    onShowReviews,
  } = props;
  const intl = useIntl();
  const bioId = useId();
  const bioRef = useRef(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [bioOverflows, setBioOverflows] = useState(false);
  const publicData = user?.attributes?.profile?.publicData || {};
  const { country, sellerType } = getProfileIdentity(publicData, userFieldConfig, intl);
  const displayedSalesCount = salesInProgress ? null : salesCount;
  const providerRatings = reviews
    .filter(review => review?.attributes?.type === REVIEW_TYPE_OF_PROVIDER)
    .map(review => review.attributes.rating)
    .filter(
      rating => typeof rating === 'number' && Number.isFinite(rating) && rating >= 1 && rating <= 5
    );
  const averageRating = providerRatings.length
    ? providerRatings.reduce((sum, rating) => sum + rating, 0) / providerRatings.length
    : null;
  const ratingPercent =
    averageRating != null
      ? intl.formatNumber(averageRating / 5, { style: 'percent', maximumFractionDigits: 0 })
      : null;
  const bioWithLinks = bio
    ? richText(bio, { linkify: true, longWordMinLength: 20, longWordClass: css.longWord })
    : null;

  useEffect(() => {
    setBioExpanded(false);
    const element = bioRef.current;
    if (!element) {
      setBioOverflows(false);
      return undefined;
    }
    const measureBio = () => {
      const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
      setBioOverflows(element.scrollHeight > lineHeight * 3 + 1);
    };
    measureBio();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measureBio);
      observer.observe(element);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measureBio);
    return () => window.removeEventListener('resize', measureBio);
  }, [bio]);

  return (
    <header className={css.root}>
      <div className={css.identity}>
        <AvatarLarge className={css.avatar} user={user} disableProfileLink />
        <div className={css.identityContent}>
          <div className={css.nameRow}>
            <h1 className={css.name}>{displayName}</h1>
            {country ? (
              <span
                className={css.country}
                role="img"
                aria-label={country.name}
                title={country.name}
              >
                <CountryFlag className={css.flag} countryCode={country.code} />
              </span>
            ) : null}
          </div>
          <dl className={css.badges}>
            <div className={css.badgeGroup}>
              <dt className={css.badgeLabel}>
                {intl.formatMessage({ id: 'ProfilePage.salesLabel' })}
              </dt>
              <dd className={css.badgeValue}>
                <span
                  className={css.salesBadge}
                  aria-busy={salesInProgress || undefined}
                  title={
                    displayedSalesCount == null
                      ? intl.formatMessage({ id: 'ProfilePage.salesUnavailable' })
                      : undefined
                  }
                  aria-label={
                    displayedSalesCount == null
                      ? intl.formatMessage({ id: 'ProfilePage.salesUnavailable' })
                      : undefined
                  }
                >
                  {displayedSalesCount == null ? '—' : intl.formatNumber(displayedSalesCount)}
                </span>
              </dd>
            </div>
            {!hideReviews ? (
              <div className={css.badgeGroup}>
                <dt className={css.badgeLabel}>
                  {intl.formatMessage({ id: 'ProfilePage.ratingLabel' })}
                </dt>
                <dd className={css.badgeValue}>
                  {queryReviewsInProgress ? (
                    <span className={css.reviewStatus} role="status">
                      {intl.formatMessage({ id: 'ProfilePage.reviewsLoading' })}
                    </span>
                  ) : queryReviewsError ? (
                    <span className={css.reviewStatus}>
                      {intl.formatMessage({ id: 'ProfilePage.loadingReviewsFailed' })}
                    </span>
                  ) : ratingPercent ? (
                    <button
                      type="button"
                      className={css.ratingBadge}
                      onClick={onShowReviews}
                      aria-label={intl.formatMessage(
                        { id: 'ProfilePage.headerRatingLabel' },
                        { rating: ratingPercent }
                      )}
                      title={intl.formatMessage(
                        { id: 'ProfilePage.reviewsSummary' },
                        { rating: intl.formatNumber(averageRating, { maximumFractionDigits: 1 }) }
                      )}
                    >
                      <span className={css.star} aria-hidden="true">
                        ★
                      </span>
                      {ratingPercent}
                    </button>
                  ) : (
                    <button type="button" className={css.noReviews} onClick={onShowReviews}>
                      {intl.formatMessage({ id: 'ProfilePage.noReviewsYet' })}
                    </button>
                  )}
                </dd>
              </div>
            ) : null}
            {sellerType ? (
              <div className={css.badgeGroup}>
                <dt className={css.badgeLabel}>
                  {intl.formatMessage({ id: 'ProfilePage.sellerTypeLabel' })}
                </dt>
                <dd className={css.badgeValue}>
                  <span
                    className={classNames(css.sellerBadge, {
                      [css.businessBadge]: sellerType === 'business',
                    })}
                  >
                    {intl.formatMessage({ id: `ProfilePage.sellerType.${sellerType}` })}
                  </span>
                </dd>
              </div>
            ) : null}
          </dl>
        </div>
        {showEditLink ? (
          <NamedLink className={css.editLink} name="ProfileSettingsPage">
            {intl.formatMessage({ id: 'ProfilePage.editProfileLinkDesktop' })}
          </NamedLink>
        ) : null}
      </div>
      {bioWithLinks ? (
        <div className={css.bioSection}>
          <p
            id={bioId}
            ref={bioRef}
            className={classNames(css.bio, { [css.bioCollapsed]: !bioExpanded })}
          >
            {bioWithLinks}
          </p>
          {bioOverflows ? (
            <button
              type="button"
              className={css.bioToggle}
              aria-controls={bioId}
              aria-expanded={bioExpanded}
              onClick={() => setBioExpanded(expanded => !expanded)}
            >
              {intl.formatMessage({
                id: bioExpanded ? 'ProfilePage.bioShowLess' : 'ProfilePage.bioShowMore',
              })}
            </button>
          ) : null}
        </div>
      ) : null}
    </header>
  );
};

export default ProfileHeader;
